import React, { useState, useEffect } from 'react';
import { Plus, BarChart3, Trash2, RefreshCw, Sparkles, Info } from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';
import { useReportWidgets } from '@hooks/useReportWidgets';
import { useWidgetExecution } from '@hooks/useWidgetExecution';
import { useNaturalLanguageWidget } from '@hooks/useNaturalLanguageWidget';
import Button from '@components/common/Button';
import ErrorBoundary from '@components/common/ErrorBoundary';
import ChartRenderer from '@components/charts/ChartRenderer';
import CreateWidgetModal from './CreateWidgetModal';
import { ReportWidget } from '@app-types/visualization';
import { formatInstantAsDate } from '@utils/dateUtils';
import styles from './AiReportsTab.module.css';

// Data access descriptions for each role
const ROLE_DATA_ACCESS_INFO: Record<string, string> = {
  Admin: 'You have full access to all data.',
  Executive: 'You have full access to all data for executive reporting.',
  Scheduler: 'You can view scheduling-related data.',
  Assessor: 'You can only see data related to your assigned assessments.',
  'Contract Role': 'You can only see data related to contracts assigned to you as Contract Owner or Contract Manager.',
  'Program Lead': 'You have full access for program oversight.'
};

/**
 * The "AI-Powered Reports" tab of the Reports page: personal chart widgets a user creates by asking in natural
 * language. These are Report Widgets, not Dashboard Widgets. The page shell supplies the layout and the tabs.
 */
const AiReportsTab: React.FC = () => {
  const { user } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [nlQuery, setNlQuery] = useState('');

  // Custom hooks - enabled for all roles (data is filtered server-side)
  const { widgets, isLoading, error, createWidget, deleteWidget, isCreating } = useReportWidgets(!!user);
  const { executeWidgets, getWidgetData, isWidgetExecuting, clearWidgetData, executeWidget } = useWidgetExecution();
  const { createWidgetFromNaturalLanguage, isGenerating } = useNaturalLanguageWidget();

  // Auto-load data for all widgets when they're loaded
  useEffect(() => {
    if (widgets.length > 0 && !isLoading) {
      executeWidgets(widgets);
    }
  }, [widgets, isLoading, executeWidgets]);

  // Handle widget creation from natural language
  const handleCreateWidget = async () => {
    try {
      await createWidgetFromNaturalLanguage(nlQuery, createWidget);
      setNlQuery('');
      setShowCreateModal(false);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to create widget');
    }
  };

  // Handle widget deletion
  const handleDeleteWidget = (widgetId: number) => {
    deleteWidget(widgetId);
    clearWidgetData(widgetId);
  };

  // Render widget content
  const renderWidgetContent = (widget: ReportWidget) => {
    const isExecuting = isWidgetExecuting(widget.id);
    const data = getWidgetData(widget.id);

    if (isExecuting) {
      return (
        <div className={styles.widgetChart}>
          <div className={styles.chartPlaceholder}>
            <RefreshCw className="animate-spin" size={32} />
            <p>Loading data...</p>
          </div>
        </div>
      );
    }

    if (!data || data.length === 0) {
      return (
        <div className={styles.widgetChart}>
          <div className={styles.chartPlaceholder}>
            <BarChart3 size={48} className="text-gray-400" />
            <p>{widget.visualization_type.toUpperCase()} Chart</p>
            <small className="text-gray-500">Data will load automatically</small>
          </div>
        </div>
      );
    }

    // Use our new ChartRenderer component
    return (
      <div className={styles.widgetContent}>
        <div className="text-center mb-2">
          <div className="flex items-center justify-center gap-2 mb-1">
            <BarChart3 size={16} className="text-green-500" />
            <span className="text-sm text-success font-semibold">
              {(widget.chartType || widget.visualization_type).toUpperCase()} Chart
            </span>
          </div>
          <small className="text-muted text-xs">{data.length} data points</small>
        </div>
        <ErrorBoundary level="page">
          <ChartRenderer widget={widget} data={data} />
        </ErrorBoundary>
      </div>
    );
  };

  // Get data access info for current roles (multi-role: show info for primary role)
  const primaryRole = user?.roles?.[0];
  const dataAccessInfo = primaryRole ? ROLE_DATA_ACCESS_INFO[primaryRole] : null;

  return (
    <div className={styles.reportsContainer} data-test-id="reports-page">
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderContent}>
          <h1 className={styles.pageTitle}>
            <Sparkles size={24} className="mr-3" />
            AI-Powered Reports Dashboard
            <span className={styles.pageCount}>{widgets?.length || 0} widgets</span>
          </h1>

          <div className={styles.pageActions}>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowCreateModal(true)}
              disabled={isLoading}
              data-test-id="create-widget-button"
            >
              <Sparkles size={16} className="mr-2" />
              Ask AI to Create Widget
            </Button>
          </div>
        </div>
        <p className="text-muted text-base mt-2">
          Create custom reports using natural language. Just describe what you want to see!
        </p>
      </div>

      {/* Data Access Info Banner */}
      {dataAccessInfo && (
        <div className={styles.dataAccessInfo}>
          <Info size={18} style={{ color: 'var(--info-color, #0284c7)', flexShrink: 0 }} />
          <span style={{ color: 'var(--info-color, #0369a1)', fontSize: '14px' }}>
            <strong>{user?.roles?.join(', ') || 'Contact'}:</strong> {dataAccessInfo}
          </span>
        </div>
      )}

      {/* Widgets Grid */}
      <div className={styles.widgetsGrid} data-test-id="widgets-grid">
        {isLoading && (
          <div className={styles.loadingState}>
            <RefreshCw className="animate-spin" size={32} />
            <p>Loading your custom reports...</p>
          </div>
        )}
        {!isLoading && error && (
          <div className={styles.errorState}>
            <p>Failed to load widgets. Please try refreshing the page.</p>
          </div>
        )}
        {!isLoading && !error && widgets.length === 0 && (
          <div className={styles.loadingState} data-test-id="empty-state">
            <BarChart3 size={48} />
            <h3>No Reports Yet</h3>
            <p>Create your first AI-powered report widget to get started!</p>
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              <Sparkles size={16} className="mr-2" />
              Create Your First Widget
            </Button>
          </div>
        )}
        {!isLoading && !error && widgets.length > 0 && (
          widgets.map((widget) => (
            <div key={widget.id} className={styles.widgetCard} data-test-id={`widget-card-${widget.id}`}>
              <div className={styles.widgetHeader}>
                <h3 className={styles.widgetTitle}>{widget.name}</h3>
                <div className={styles.widgetActions}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => executeWidget(widget)}
                    disabled={isWidgetExecuting(widget.id)}
                    className={styles.deleteButton}
                  >
                    <RefreshCw size={14} className={isWidgetExecuting(widget.id) ? 'animate-spin' : ''} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteWidget(widget.id)}
                    disabled={isCreating}
                    className={styles.deleteButton}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>

              <div className={styles.widgetContent} data-test-id={`widget-content-${widget.id}`}>
                {renderWidgetContent(widget)}
              </div>

              <div className={styles.widgetFooter}>
                <div className="flex justify-between items-center">
                  <small className={styles.widgetMeta}>
                    Created {formatInstantAsDate(widget.created_at)}
                  </small>
                  {getWidgetData(widget.id) && <small className="text-success text-xs">✓ Data loaded</small>}
                </div>
              </div>
            </div>
          ))
        )}

        {/* Add Widget Placeholder */}
        <button
          type="button"
          className={`${styles.widgetCard} ${styles.addWidgetCard}`}
          onClick={() => setShowCreateModal(true)}
          data-test-id="add-widget-card"
        >
          <div className={styles.addWidgetContent}>
            <Plus size={48} className={styles.addIcon} />
            <h3>Create New Widget</h3>
            <p>Ask AI to generate a custom report widget</p>
          </div>
        </button>
      </div>

      {/* Create Widget Modal */}
      <CreateWidgetModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        nlQuery={nlQuery}
        setNlQuery={setNlQuery}
        isGenerating={isGenerating}
        onCreateWidget={handleCreateWidget}
      />
    </div>
  );
};

export { AiReportsTab };
